/**
 * google-fonts-service — fetches the Google Fonts catalog and downloads
 * individual font files for brand asset storage.
 *
 * Uses the official Webfonts API v1 (requires GOOGLE_FONTS_API_KEY env var):
 *   - Catalog:  https://www.googleapis.com/webfonts/v1/webfonts?key=KEY
 *   - Files:    direct gstatic URLs returned in the catalog response
 *
 * `capability=WOFF2` is requested so every variant is downloaded as .woff2 —
 * one canonical extension across the brand assets folder, no ttf/otf/woff
 * collisions when re-picking fonts.
 *
 * The catalog is cached in memory for 1 hour to avoid hammering the API.
 */

import { writeFile, mkdir, readdir, unlink } from "node:fs/promises";
import { join, extname } from "node:path";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GoogleFontVariant {
  weight: string;
  italic: boolean;
}

export interface GoogleFontFamily {
  family: string;
  category: string;
  variants: GoogleFontVariant[];
  popularity: number;
  designers: string[];
  dateAdded: string;
  lastModified: string;
}

export interface GoogleFontsCatalog {
  families: GoogleFontFamily[];
  totalFonts: number;
}

// Internal — keeps the raw `files` map per family so downloadGoogleFont
// can resolve a variant → URL without a second API roundtrip.
interface CatalogIndexEntry {
  family: GoogleFontFamily;
  files: Record<string, string>; // variantKey ("regular", "700italic", ...) → gstatic URL
}

// ---------------------------------------------------------------------------
// Catalog — fetch + cache
// ---------------------------------------------------------------------------

const API_BASE = "https://www.googleapis.com/webfonts/v1/webfonts";
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

let cachedCatalog: GoogleFontsCatalog | null = null;
let cachedIndex: Map<string, CatalogIndexEntry> | null = null;
let cacheTimestamp = 0;

const CATEGORY_MAP: Record<string, string> = {
  "sans-serif": "Sans Serif",
  serif: "Serif",
  display: "Display",
  handwriting: "Handwriting",
  monospace: "Monospace",
};

function normalizeCategory(raw: string): string {
  return CATEGORY_MAP[raw] ?? "Other";
}

/**
 * Convert a v1 API variant string → { weight, italic }.
 * Examples: "regular" → 400, "italic" → 400i, "700" → 700, "700italic" → 700i
 */
function parseVariantString(v: string): GoogleFontVariant {
  if (v === "regular") return { weight: "400", italic: false };
  if (v === "italic") return { weight: "400", italic: true };
  if (v.endsWith("italic")) {
    return { weight: v.slice(0, -"italic".length), italic: true };
  }
  return { weight: v, italic: false };
}

/**
 * Inverse of parseVariantString — used to look up a `files` entry.
 */
function variantKey(weight: string, italic: boolean): string {
  if (weight === "400") return italic ? "italic" : "regular";
  return italic ? `${weight}italic` : weight;
}

function getApiKey(): string {
  const key = process.env.GOOGLE_FONTS_API_KEY?.trim();
  if (!key) {
    throw new Error(
      "GOOGLE_FONTS_API_KEY env var is not set. Add it to packages/server/.env (see .env.example).",
    );
  }
  return key;
}

export async function fetchGoogleFontsCatalog(): Promise<GoogleFontsCatalog> {
  if (cachedCatalog && Date.now() - cacheTimestamp < CACHE_TTL_MS) {
    return cachedCatalog;
  }

  const key = getApiKey();
  const url = `${API_BASE}?key=${encodeURIComponent(key)}&sort=popularity&capability=WOFF2`;
  const res = await fetch(url);
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(
      `Google Fonts API fetch failed: ${res.status} ${res.statusText}${body ? ` — ${body.slice(0, 200)}` : ""}`,
    );
  }

  const data = (await res.json()) as {
    items: Array<{
      family: string;
      category: string;
      variants: string[];
      files: Record<string, string>;
      lastModified: string;
    }>;
  };

  const families: GoogleFontFamily[] = [];
  const index = new Map<string, CatalogIndexEntry>();

  data.items.forEach((entry, i) => {
    const variants = entry.variants
      .map(parseVariantString)
      .sort((a, b) => Number(a.weight) - Number(b.weight));

    const family: GoogleFontFamily = {
      family: entry.family,
      category: normalizeCategory(entry.category),
      variants,
      popularity: i, // v1 with sort=popularity returns sorted; rank = index
      designers: [],
      dateAdded: "",
      lastModified: entry.lastModified ?? "",
    };

    families.push(family);
    index.set(entry.family, { family, files: entry.files });
  });

  const catalog: GoogleFontsCatalog = {
    families,
    totalFonts: families.reduce((n, f) => n + f.variants.length, 0),
  };

  cachedCatalog = catalog;
  cachedIndex = index;
  cacheTimestamp = Date.now();
  return catalog;
}

// ---------------------------------------------------------------------------
// Download — pull a specific variant via the v1 catalog's `files` map
// ---------------------------------------------------------------------------

/**
 * Download a Google Font variant and save it to a destination path.
 *
 * 1. Ensure the catalog (with `files` URLs) is loaded.
 * 2. Look up the family + variant key in the cached index.
 * 3. Fetch the font binary from the gstatic URL the API gave us.
 * 4. Write it to destPath as `${targetName}.woff2` (canonical extension).
 *
 * Any pre-existing file with the same `targetName` but a different extension
 * (e.g. a leftover .ttf from a prior RightFont selection) is removed first,
 * so the assets folder never has two files for the same slot.
 *
 * Returns the written file path.
 */
export async function downloadGoogleFont(args: {
  family: string;
  weight: string;
  italic: boolean;
  destDir: string;
  targetName: string;
}): Promise<{ filePath: string; fileName: string }> {
  const { family, weight, italic, destDir, targetName } = args;

  // Ensure catalog + index are loaded
  await fetchGoogleFontsCatalog();
  if (!cachedIndex) {
    throw new Error("Google Fonts catalog index unavailable after fetch");
  }

  const entry = cachedIndex.get(family);
  if (!entry) {
    throw new Error(`Family "${family}" not found in Google Fonts catalog`);
  }

  const key = variantKey(weight, italic);
  let fontUrl = entry.files[key];

  // Fallback: if the exact variant isn't there, pick the nearest available
  // weight with the same italic flag, then the nearest weight of the other
  // italic flag. (Some families omit specific italic/weight combos.)
  if (!fontUrl) {
    const wanted = Number(weight) || 400;
    const parsed = Object.keys(entry.files).map((k) => ({ key: k, ...parseVariantString(k) }));
    const byDistance = (candidates: typeof parsed) =>
      candidates.sort(
        (a, b) => Math.abs(Number(a.weight) - wanted) - Math.abs(Number(b.weight) - wanted),
      )[0];
    const sameItalic = byDistance(parsed.filter((v) => v.italic === italic));
    const otherItalic = byDistance(parsed.filter((v) => v.italic !== italic));
    const best = sameItalic ?? otherItalic;
    if (best) fontUrl = entry.files[best.key];
  }

  if (!fontUrl) {
    throw new Error(
      `No downloadable file for "${family}" ${weight}${italic ? "i" : ""} — variants available: ${Object.keys(entry.files).join(", ")}`,
    );
  }

  // The v1 API returns http:// URLs by default; force https for safety.
  if (fontUrl.startsWith("http://")) fontUrl = `https://${fontUrl.slice("http://".length)}`;

  const ext = extname(new URL(fontUrl).pathname) || ".woff2";
  const fileName = `${targetName}${ext}`;

  const fontRes = await fetch(fontUrl);
  if (!fontRes.ok) {
    throw new Error(`Font file download failed: ${fontRes.status} from ${fontUrl}`);
  }

  const buffer = Buffer.from(await fontRes.arrayBuffer());

  // Validate magic bytes so an HTML error page or truncated response never
  // gets written as a brand asset (it would silently break every renderer).
  const magic = buffer.subarray(0, 4).toString("latin1");
  const magicBE = buffer.readUInt32BE(0);
  const isFont =
    magic === "wOF2" || // woff2
    magic === "wOFF" || // woff
    magic === "OTTO" || // otf
    magic === "true" || // legacy ttf
    magicBE === 0x00010000; // ttf
  if (buffer.length < 4 || !isFont) {
    throw new Error(
      `Downloaded data for "${family}" ${weight}${italic ? "i" : ""} is not a font file (${buffer.length} bytes from ${fontUrl})`,
    );
  }

  await mkdir(destDir, { recursive: true });

  // Remove any existing files with the same targetName but a different
  // extension so we never end up with both typeface-hero.ttf AND .woff2.
  try {
    const existing = await readdir(destDir);
    for (const f of existing) {
      if (f.startsWith(`${targetName}.`) && f !== fileName) {
        await unlink(join(destDir, f));
      }
    }
  } catch {
    /* best effort */
  }

  const filePath = join(destDir, fileName);
  await writeFile(filePath, buffer);

  return { filePath, fileName };
}
